import { useEffect, useState, type FormEvent } from 'react';
import { api, type Business, type Credential, type PendingInvite, type Role, type TeamMember } from '../api';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function Team({ canInvite, canManageKeys }: { canInvite: boolean; canManageKeys: boolean }) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reload = () =>
    Promise.all([api.teamMembers(), api.teamInvites()])
      .then(([m, i]) => {
        setMembers(m);
        setInvites(i);
      })
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void reload();
  }, []);

  const columns: Column<TeamMember>[] = [
    { key: 'name', title: t('team.col.name'), render: (m) => m.displayName, sortValue: (m) => m.displayName },
    { key: 'type', title: t('team.col.type'), render: (m) => <span className="badge">{t(m.type === 'human' ? 'team.type.human' : 'team.type.agent')}</span>, sortValue: (m) => m.type },
    { key: 'email', title: t('team.col.email'), render: (m) => m.email ?? '—', sortValue: (m) => m.email },
    { key: 'roles', title: t('team.col.roles'), render: (m) => m.roles.join(', ') || '—', wrap: true },
    { key: 'businesses', title: t('team.col.businesses'), render: (m) => m.businesses.join(', ') || '—', wrap: true },
    { key: '2fa', title: t('team.col.2fa'), render: (m) => (m.type === 'human' ? t(m.twoFactorEnabled ? 'common.yes' : 'common.no') : '—'), sortValue: (m) => (m.twoFactorEnabled ? 1 : 0) },
    { key: 'last', title: t('team.col.lastLogin'), render: (m) => fmt(m.lastLoginAt), sortValue: (m) => m.lastLoginAt },
  ];

  return (
    <>
      <h1>{t('team.title')}</h1>
      {error && <div className="error">{error}</div>}
      <h2>{t('team.members')}</h2>
      <Table columns={columns} rows={members} rowKey={(m) => m.actorId} empty={t('common.none')} />

      <h2>{t('team.invites')}</h2>
      {canInvite && <InviteForm onCreated={reload} />}
      {invites.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--sp-4)' }}>
          <div className="muted">{t('team.invite.pending')}</div>
          <ul>
            {invites.map((i) => (
              <li key={i.id}>
                {i.email} · {i.role?.name ?? '—'} · {t('team.invite.expires')} {fmt(i.expiresAt)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {canManageKeys && <Keys />}
    </>
  );
}

function InviteForm({ onCreated }: { onCreated: () => void }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [selected, setSelected] = useState<string[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.teamRoles().then((r) => setRoles(r.filter((x) => x.kind !== 'agent'))).catch(() => undefined);
    api.teamBusinesses().then(setBusinesses).catch(() => undefined);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setLink(null);
    try {
      const r = await api.createInvite(email, role, selected);
      setLink(r.inviteUrl);
      setEmail('');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable: the link is visible as text */
    }
  };

  return (
    <form className="form card" onSubmit={submit}>
      <strong>{t('team.invite.new')}</strong>
      <label>
        {t('team.invite.email')}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label>
        {t('team.invite.role')}
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((r) => (
            <option key={r.id} value={r.name}>{r.name}{r.description ? ` — ${r.description}` : ''}</option>
          ))}
        </select>
      </label>
      {businesses.length > 0 && (
        <div className="field">
          {t('team.invite.businesses')}
          <div className="checks">
            {businesses.map((b) => (
              <label key={b.id}>
                <input type="checkbox" checked={selected.includes(b.id)} onChange={(e) => setSelected((s) => (e.target.checked ? [...s, b.id] : s.filter((x) => x !== b.id)))} />
                {b.name}
              </label>
            ))}
          </div>
        </div>
      )}
      {error && <div className="error">{error}</div>}
      <div className="row">
        <button className="btn" type="submit" disabled={busy}>{t('team.invite.submit')}</button>
      </div>
      {link && (
        <div className="stack">
          <div className="muted">{t('team.invite.link')}</div>
          <div className="row">
            <code className="mono">{link}</code>
            <button type="button" className="btn secondary small" onClick={copy}>{copied ? t('common.copied') : t('common.copy')}</button>
          </div>
        </div>
      )}
    </form>
  );
}

function Keys() {
  const [items, setItems] = useState<Credential[]>([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [provider, setProvider] = useState('anthropic');
  const [name, setName] = useState('');
  const [secret, setSecret] = useState('');
  const [scope, setScope] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState<{ id: string; secret: string } | null>(null);

  const reload = () => api.credentials().then(setItems).catch((e: Error) => setError(e.message));
  useEffect(() => {
    void reload();
    api.credentialProviders().then(setProviders).catch(() => undefined);
  }, []);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createCredential({ provider, name, secret, scope: scope || undefined });
      setName('');
      setSecret('');
      setScope('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    if (!rotating || !rotating.secret) return;
    await api.rotateCredential(rotating.id, rotating.secret).catch((e: Error) => setError(e.message));
    setRotating(null);
    await reload();
  };

  const revoke = async (id: string) => {
    if (!window.confirm(t('team.keys.confirmRevoke'))) return;
    await api.revokeCredential(id).catch((e: Error) => setError(e.message));
    await reload();
  };

  const columns: Column<Credential>[] = [
    { key: 'provider', title: t('team.keys.provider'), render: (c) => c.provider, sortValue: (c) => c.provider },
    { key: 'name', title: t('team.keys.name'), render: (c) => c.name, sortValue: (c) => c.name },
    { key: 'scope', title: t('team.keys.scope'), render: (c) => c.scope ?? '—', wrap: true },
    {
      key: 'state',
      title: '',
      render: (c) => (c.revokedAt ? <span className="badge">{t('team.keys.revoked')} {fmt(c.revokedAt)}</span> : c.rotatedAt ? <span className="badge">{t('team.keys.rotated')} {fmt(c.rotatedAt)}</span> : <span className="badge">{t('team.keys.created')} {fmt(c.createdAt)}</span>),
      sortValue: (c) => c.revokedAt ?? c.rotatedAt ?? c.createdAt,
    },
    {
      key: 'actions',
      title: '',
      render: (c) =>
        c.revokedAt ? null : rotating?.id === c.id ? (
          <span className="row">
            <input className="input" type="password" placeholder={t('team.keys.secret')} value={rotating.secret} onChange={(e) => setRotating({ id: c.id, secret: e.target.value })} style={{ width: 180 }} />
            <button type="button" className="btn small" onClick={rotate}>{t('common.save')}</button>
            <button type="button" className="btn secondary small" onClick={() => setRotating(null)}>{t('common.cancel')}</button>
          </span>
        ) : (
          <span className="row">
            <button type="button" className="btn secondary small" onClick={() => setRotating({ id: c.id, secret: '' })}>{t('team.keys.rotate')}</button>
            <button type="button" className="btn danger small" onClick={() => revoke(c.id)}>{t('team.keys.revoke')}</button>
          </span>
        ),
    },
  ];

  return (
    <>
      <h2>{t('team.keys')}</h2>
      <p className="muted">{t('team.keys.hint')}</p>
      <Table columns={columns} rows={items} rowKey={(c) => c.id} empty={t('common.none')} />
      <form className="form card" onSubmit={add} style={{ marginTop: 'var(--sp-4)' }}>
        <strong>{t('team.keys.add')}</strong>
        <label>
          {t('team.keys.provider')}
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            {providers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label>
          {t('team.keys.name')}
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </label>
        <label>
          {t('team.keys.secret')}
          <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} required autoComplete="off" />
        </label>
        <label>
          {t('team.keys.scope')}
          <input value={scope} onChange={(e) => setScope(e.target.value)} maxLength={200} />
        </label>
        {error && <div className="error">{error}</div>}
        <div className="row">
          <button className="btn" type="submit" disabled={busy}>{t('common.save')}</button>
        </div>
      </form>
    </>
  );
}
